import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, CardActionArea, Typography } from '@mui/material';
import GlassCard from './GlassCard';

/**
 * Compact dashboard tile: label, primary value, optional hint. Navigates when `to` is set.
 *
 * @param {object} props
 * @param {string} [props.to]
 * @param {React.ReactNode} props.icon
 * @param {string} props.label
 * @param {string | number} props.value
 * @param {string} [props.hint]
 * @param {boolean} [props.loading]
 */
const DashboardMetricCard = ({ to, icon, label, value, hint, loading }) => {
  const content = (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
        width: '100%',
        minHeight: 108,
      }}
    >
      <Box
        sx={{
          p: 1.125,
          borderRadius: 1.5,
          bgcolor: 'action.hover',
          color: 'primary.main',
          display: 'flex',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: 'block', letterSpacing: '0.08em', fontWeight: 600, lineHeight: 1.4, mb: 0.75 }}
        >
          {label}
        </Typography>
        <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: '-0.03em', lineHeight: 1.15 }}>
          {loading ? '…' : value}
        </Typography>
        {hint && !loading ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, lineHeight: 1.4 }}>
            {hint}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );

  return (
    <GlassCard hoverable={Boolean(to)} sx={{ height: '100%', borderRadius: 2 }}>
      {to ? (
        <CardActionArea
          component={RouterLink}
          to={to}
          sx={{
            p: 2.5,
            height: '100%',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            borderRadius: 2,
            '&:hover .MuiCardActionArea-focusHighlight': { opacity: 0 },
          }}
        >
          {content}
        </CardActionArea>
      ) : (
        <Box sx={{ p: 2.5 }}>{content}</Box>
      )}
    </GlassCard>
  );
};

export default DashboardMetricCard;
